import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import { useNavigate } from 'react-router-dom';
import { UnitOfCompetency } from '@duke-hq/svc-api-db';
import { Ellipsis, Pencil, Trash2 } from 'lucide-react';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { useMemo, useState } from 'react';
import { UnitsOfCompetencyTableFilters } from './unitsOfCompetencyTableFilters';
import { SortOrder } from '@duke-hq/lib/pagination';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import {
  UnitCoverageBadge,
  type ResourceCoverageLike,
} from '../components/translations/TranslationCoverageBadge';

/** The list query includes the category relation, which the bare Prisma model omits. */
type UnitOfCompetencyRow = UnitOfCompetency & {
  unitContextCategory?: { categoryName: string } | null;
};

interface UnitOfCompetencyProps {
  unitsOfCompetency: UnitOfCompetencyRow[];
  handleUnitDelete: (id: string) => void;
  onFiltersChange: (filters: UnitsOfCompetencyTableFilters) => void;
  filters: UnitsOfCompetencyTableFilters;
}

export function UnitsOfCompetencyTable({
  unitsOfCompetency,
  handleUnitDelete,
  filters,
  onFiltersChange,
}: UnitOfCompetencyProps): React.ReactElement {
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const navigate = useNavigate();

  // One batch query per page so each row's badge needn't fetch its own coverage.
  const unitIds = useMemo(() => unitsOfCompetency.map((unit) => unit.id), [unitsOfCompetency]);
  const { data: coverage } = trpcAdmin.contentTranslations.listCoverageByUnits.useQuery(
    { unitIds },
    { enabled: unitIds.length > 0 },
  );
  const coverageByUnitId = useMemo(
    () =>
      new Map<string, ResourceCoverageLike[]>(
        (coverage?.units ?? []).map((unit) => [unit.unitId, unit.resources]),
      ),
    [coverage],
  );

  const handleSort = (name: UnitsOfCompetencyTableFilters['orderBy']) => {
    let newSort = filters.sort;
    if (filters.orderBy === name) {
      newSort = newSort === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
    }

    onFiltersChange({ ...filters, orderBy: name, sort: newSort });
  };

  return (
    <>
      <Table layout="auto">
        <Thead>
          <Tr>
            <Th>
              <SortableHeader
                label={t`Code`}
                columnName="code"
                currentOrderBy={filters.orderBy}
                currentSortOrder={filters.sort}
                onSortChange={() => handleSort('code')}
              />
            </Th>
            <Th>
              <SortableHeader
                label={t`Title`}
                columnName="title"
                currentOrderBy={filters.orderBy}
                currentSortOrder={filters.sort}
                onSortChange={() => handleSort('title')}
              />
            </Th>
            <Th>
              <SortableHeader
                label={t`Created at`}
                columnName="created_at"
                currentOrderBy={filters.orderBy}
                currentSortOrder={filters.sort}
                onSortChange={() => handleSort('created_at')}
              />
            </Th>
            <Th>
              <Trans>Translations</Trans>
            </Th>
            <Th position="sticky" right={0} zIndex={2} bg="bg.surface !important" px={8}>
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                boxShadow="-0.25rem 0 0.375rem rgba(0, 0, 0, 0.1)"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Ellipsis size={iconSize.lg} />
              </Box>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {unitsOfCompetency.map((unit) => {
            const coverage = coverageByUnitId.get(unit.id);
            return (
              <Tr
                key={unit.id}
                onClick={() => navigate(`${unit.id}`)}
                cursor="pointer"
                _hover={{ bg: 'bg.subtle' }}
              >
                <Td>
                  <Tooltip label={unit.code} hasArrow placement="top-start" fontSize="xs">
                    <Text overflow="hidden" textOverflow="ellipsis">
                      {unit.code}
                    </Text>
                  </Tooltip>
                </Td>
                <Td>
                  <Flex align="center" justify="start" gap="2">
                    <Tooltip label={unit.title} hasArrow placement="top-start" fontSize="xs">
                      <Text fontWeight="medium" overflow="hidden" textOverflow="ellipsis">
                        {unit.title}
                      </Text>
                    </Tooltip>
                    {unit?.unitContextCategory?.categoryName ? (
                      <Tag size="sm" variant="solid" colorScheme="blackAlpha">
                        {unit.unitContextCategory.categoryName}
                      </Tag>
                    ) : null}
                  </Flex>
                </Td>
                <Td>
                  <Text color="fg.muted">{djs(unit.created_at).format('dddd, DD MMMM YYYY')}</Text>
                </Td>
                <Td>
                  {coverage ? (
                    <UnitCoverageBadge resources={coverage} showUnpublishedAsNotTranslated />
                  ) : null}
                </Td>
                <Td position="sticky" right={0} zIndex={1} bg="bg.surface" px={8}>
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    boxShadow="-0.25rem 0 0.375rem rgba(0, 0, 0, 0.1)"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        aria-label="Options"
                        icon={<Ellipsis size={iconSize.lg} />}
                        variant="ghost"
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                        display="inline-flex"
                      />
                      <Portal>
                        <MenuList>
                          <MenuItem
                            icon={<Pencil size={iconSize.md} />}
                            aria-label={t`Edit unit of competency`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`${unit.id}`);
                            }}
                          >
                            <Trans>Edit</Trans>
                          </MenuItem>
                          <MenuItem
                            icon={<Trash2 size={iconSize.md} />}
                            aria-label={t`Delete unit of competency`}
                            color="danger"
                            bg="red.50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsAlertOpen(true);
                              setSelectedUnitId(unit.id);
                            }}
                          >
                            <Trans>Delete</Trans>
                          </MenuItem>
                        </MenuList>
                      </Portal>
                    </Menu>
                  </Box>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>

      <ConfirmationDialog
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        title={<Trans>Delete Unit of Competency</Trans>}
        body={
          <Box>
            <Trans>
              You are about to delete this unit of competency. Are you sure you want to continue?
            </Trans>
            {selectedUnitId ? (
              <Box
                mt={4}
                p={4}
                borderColor="danger.subtle"
                bg="bg.danger-subtle"
                borderRadius="8px"
              >
                <Text fontSize="sm" fontWeight="medium">
                  {unitsOfCompetency.find((unit) => unit.id === selectedUnitId)?.title}
                </Text>
              </Box>
            ) : null}
          </Box>
        }
        onConfirm={() => {
          if (selectedUnitId) {
            handleUnitDelete(selectedUnitId);
          }
          setIsAlertOpen(false);
        }}
        confirmLabel={<Trans>Delete</Trans>}
        cancelLabel={<Trans>Cancel</Trans>}
        variant="destructive"
      />
    </>
  );
}
